import { Box, CircularProgress, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import theme from "@/theme";

import { CustomButton } from "./Button";

const useStyles = makeStyles(() => ({
  panel: {
    padding: "32px 24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
    borderRadius: 16,
    backgroundColor: theme.palette.semantic["surface/card"],
    textAlign: "center",
  },
  empty: { padding: "40px 24px" },
  texts: { display: "flex", flexDirection: "column", gap: 8, width: "100%" },
  title: { fontSize: 16, fontWeight: 700, lineHeight: 1.3 },
  description: { fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-line", color: theme.palette.text.secondary },
  fullPage: {
    minHeight: "100vh",
    padding: "32px 24px",
    paddingTop: "calc(32px + env(safe-area-inset-top, 0px))",
    paddingBottom: "calc(32px + env(safe-area-inset-bottom, 0px))",
    display: "flex",
    flexDirection: "column",
    gap: 24,
    backgroundColor: theme.palette.semantic["surface/card"],
  },
  publicPage: {
    [theme.breakpoints.up("md")]: {
      minHeight: 0,
      width: 560,
      padding: "40px 48px",
      margin: "48px auto 64px",
      borderRadius: 16,
      gap: 32,
      "& h2": { fontSize: 20 },
      "& $description": { fontSize: 16 },
    },
  },
  publicAction: { [theme.breakpoints.up("md")]: { maxWidth: 360 } },
  fullContent: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 24,
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  fullInner: { maxWidth: 480, width: "100%", alignSelf: "center" },
  illustration: { width: 160, height: 160, display: "flex", alignItems: "center", justifyContent: "center" },
}));

interface StatePanelProps {
  title?: string;
  description?: React.ReactNode;
  illustration?: React.ReactNode;
  action?: React.ReactNode;
}

const StateTexts: React.FC<Pick<StatePanelProps, "title" | "description">> = ({ title, description }) => {
  const classes = useStyles();
  return (
    <Box className={classes.texts}>
      {title && (
        <Typography component="h2" className={classes.title}>
          {title}
        </Typography>
      )}
      {description && (
        <Typography component="div" className={classes.description}>
          {description}
        </Typography>
      )}
    </Box>
  );
};

export const ErrorPanel: React.FC<Omit<StatePanelProps, "action"> & { retryLabel: string; onRetry: () => void }> = ({
  retryLabel,
  onRetry,
  title,
  description,
  illustration,
}) => {
  const classes = useStyles();
  return (
    <Box className={classes.panel}>
      {illustration}
      <StateTexts title={title} description={description} />
      <CustomButton tone="outline" buttonSize="M" fullWidth={false} onClick={onRetry}>
        {retryLabel}
      </CustomButton>
    </Box>
  );
};

export const EmptyPanel: React.FC<StatePanelProps> = ({ title, description, illustration, action }) => {
  const classes = useStyles();
  return (
    <Box className={`${classes.panel} ${classes.empty}`}>
      {illustration}
      <StateTexts title={title} description={description} />
      {action}
    </Box>
  );
};

export const LoadingPanel: React.FC<{ message: string }> = ({ message }) => {
  const classes = useStyles();
  return (
    <Box className={`${classes.panel} ${classes.empty}`} role="status" aria-live="polite">
      <CircularProgress size={40} aria-label={message} />
      <Typography className={classes.description}>{message}</Typography>
    </Box>
  );
};

/** 共通ヘッダーや下タブを持たない全画面型。文言・画像・操作は各フローから渡す。 */
export const FullPageError: React.FC<StatePanelProps & { publicLayout?: boolean }> = ({
  title,
  description,
  illustration,
  action,
  publicLayout,
}) => {
  const classes = useStyles();
  return (
    <Box component="main" className={`${classes.fullPage} ${publicLayout ? classes.publicPage : ""}`}>
      <Box className={classes.fullContent}>
        {illustration && <Box className={classes.illustration}>{illustration}</Box>}
        <Box className={classes.fullInner}>
          <StateTexts title={title} description={description} />
        </Box>
      </Box>
      {action && <Box className={`${classes.fullInner} ${publicLayout ? classes.publicAction : ""}`}>{action}</Box>}
    </Box>
  );
};
